import { useState, useEffect } from "react";
import { CheckCircle, XCircle, Save, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useLocation } from "react-router-dom";
import apiClient from "@/lib/api";

export default function AttendancePage() {
  const [sessions, setSessions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [attendance, setAttendance] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    Promise.all([apiClient.get("/sessions"), apiClient.get("/players")])
      .then(([sessRes, playRes]) => {
        setSessions(sessRes.data);
        setPlayers(playRes.data);
        const preSelected = location.state?.session_id;
        if (preSelected) {
          setSelectedSession(preSelected);
        } else if (sessRes.data.length > 0) {
          setSelectedSession(sessRes.data[0].id);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedSession || players.length === 0) return;
    apiClient
      .get(`/attendance?session_id=${selectedSession}`)
      .then((r) => {
        const map = {};
        players.forEach((p) => { map[p.id] = false; });
        r.data.forEach((rec) => { map[rec.player_id] = rec.present; });
        setAttendance(map);
        setSaved(false);
      })
      .catch(() => {
        const map = {};
        players.forEach((p) => { map[p.id] = false; });
        setAttendance(map);
      });
  }, [selectedSession, players]);

  const togglePlayer = (id) => {
    setAttendance((prev) => ({ ...prev, [id]: !prev[id] }));
    setSaved(false);
  };

  const markAll = (present) => {
    const map = {};
    players.forEach((p) => { map[p.id] = present; });
    setAttendance(map);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!selectedSession) return;
    setSaving(true);
    try {
      const records = players.map((p) => ({
        player_id: p.id,
        present: attendance[p.id] || false,
      }));
      await apiClient.post("/attendance", { session_id: selectedSession, records });
      setSaved(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const presentCount = Object.values(attendance).filter(Boolean).length;
  const selectedSessionData = sessions.find((s) => s.id === selectedSession);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="attendance-page">
      <div className="mb-8">
        <h1
          className="text-3xl font-black text-gray-900"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Attendance
        </h1>
        <p className="text-gray-600 mt-1">Mark player attendance for each training session</p>
      </div>

      <div className="bg-white rounded-sm border border-gray-200 p-6 mb-6">
        <Label className="text-sm font-semibold">Select Session</Label>
        {sessions.length === 0 ? (
          <p className="text-sm text-gray-500 mt-2">No sessions yet. Log a session first.</p>
        ) : (
          <Select value={selectedSession} onValueChange={(v) => setSelectedSession(v)}>
            <SelectTrigger className="mt-2 max-w-md" data-testid="session-select">
              <SelectValue placeholder="Choose a session..." />
            </SelectTrigger>
            <SelectContent>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.title} — {s.date}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {selectedSessionData && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
            <span>{selectedSessionData.drill_type}</span>
            <span>·</span>
            <span>{selectedSessionData.intensity} intensity</span>
            <span>·</span>
            <span>{selectedSessionData.duration_minutes} min</span>
          </div>
        )}
      </div>

      {selectedSession && players.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-700">
              <span className="text-green-600 font-black">{presentCount}</span>/{players.length} players present
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAll(true)}
                className="rounded-sm text-xs border-gray-200"
                data-testid="mark-all-present-btn"
              >
                All Present
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAll(false)}
                className="rounded-sm text-xs border-gray-200"
                data-testid="mark-all-absent-btn"
              >
                All Absent
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                data-testid="save-attendance-btn"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" />
                {saving ? "Saving..." : saved ? "Saved!" : "Save"}
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
            {players.map((player, i) => (
              <div
                key={player.id}
                onClick={() => togglePlayer(player.id)}
                className={`flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  i > 0 ? "border-t border-gray-100" : ""
                } ${attendance[player.id] ? "bg-green-50/40" : ""}`}
                data-testid={`attendance-row-${player.id}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      attendance[player.id]
                        ? "bg-green-600 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {player.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{player.name}</div>
                    <div className="text-xs text-gray-500">
                      {player.position} · #{player.jersey_number || "–"}
                    </div>
                  </div>
                </div>
                <div>
                  {attendance[player.id] ? (
                    <div className="flex items-center gap-1.5 text-green-600 text-sm font-semibold">
                      <CheckCircle className="w-5 h-5" />
                      Present
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-gray-400 text-sm">
                      <XCircle className="w-5 h-5" />
                      Absent
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedSession && players.length === 0 && (
        <div className="text-center py-16 bg-white rounded-sm border border-gray-200">
          <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">No players added yet. Go to Players to add your squad.</p>
        </div>
      )}
    </div>
  );
}
